import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import RatePlanDraftFields from './RatePlanDraftFields';
import { getRatePlanDraftLabel, isCreatedRatePlanDraft } from './utils';
import type { FlashRatePlanDraft, RatePlanCreateSchema } from './utils';

interface Props {
  disabled: boolean;
  expandedRatePlanDraftIds: Array<string>;
  hasSchemaError: boolean;
  isLoadingSchema: boolean;
  onAddDraft: () => void;
  onClearError: () => void;
  onRemoveDraft: (draftId: string) => void;
  onRetrySchema: () => void;
  onToggleDraftExpanded: (draftId: string, expanded: boolean) => void;
  onUpdateDraft: (draftId: string, updates: Partial<FlashRatePlanDraft>) => void;
  ratePlanCreateSchema: RatePlanCreateSchema | undefined;
  ratePlanDrafts: Array<FlashRatePlanDraft>;
  readOnly?: boolean;
}

export default function RatePlanStep({
  disabled,
  expandedRatePlanDraftIds,
  hasSchemaError,
  isLoadingSchema,
  onAddDraft,
  onClearError,
  onRemoveDraft,
  onRetrySchema,
  onToggleDraftExpanded,
  onUpdateDraft,
  ratePlanCreateSchema,
  ratePlanDrafts,
  readOnly = false,
}: Props) {
  const hasCreatedDrafts = ratePlanDrafts.some(isCreatedRatePlanDraft);
  const removableDraftCount = ratePlanDrafts.filter((draft) => !isCreatedRatePlanDraft(draft)).length;

  return (
    <Stack spacing={2}>
      {!isLoadingSchema && !hasSchemaError && (
        <Alert severity="info">
          {readOnly
            ? 'These Flash rate plans have already been created. Review them here, or continue to the next step.'
            : hasCreatedDrafts
              ? 'Some rate plans were already created and are locked. Fix any remaining plans and click Next to retry.'
              : 'Add, collapse, or remove rate plans as needed. At least one rate plan is required. Click Next to create them and apply them to rooms.'}
        </Alert>
      )}
      {isLoadingSchema && (
        <Alert icon={<CircularProgress size={16} />} severity="info">
          Loading rate plan details.
        </Alert>
      )}
      {!isLoadingSchema && hasSchemaError && (
        <Alert
          severity="error"
          action={
            <Button disabled={disabled} onClick={onRetrySchema}>
              Try again
            </Button>
          }
        >
          Unable to load rate plan details.
        </Alert>
      )}
      {!isLoadingSchema && !hasSchemaError && (
        <>
          {ratePlanDrafts.map((draft, index) => {
            const label = getRatePlanDraftLabel(draft, index);
            const isExpanded = expandedRatePlanDraftIds.includes(draft.draftId);
            const isDraftLocked = readOnly || isCreatedRatePlanDraft(draft);

            return (
              <Accordion
                key={draft.draftId}
                expanded={isExpanded}
                onChange={(_, expanded) => onToggleDraftExpanded(draft.draftId, expanded)}
                disabled={disabled}
                disableGutters
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%', pr: 1 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {label}
                      </Typography>
                      {draft.createdRatePlanId && (
                        <Chip label="Created" size="small" color="success" variant="outlined" />
                      )}
                    </Stack>
                    {!readOnly && (
                      <IconButton
                        aria-label={`Remove ${label}`}
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRemoveDraft(draft.draftId);
                        }}
                        disabled={disabled || isDraftLocked || removableDraftCount <= 1}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <RatePlanDraftFields
                    disabled={disabled || isDraftLocked}
                    draft={draft}
                    onClearError={onClearError}
                    onUpdateDraft={(updates) => onUpdateDraft(draft.draftId, updates)}
                    ratePlanCreateSchema={ratePlanCreateSchema}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
          {!readOnly && (
            <Box>
              <Button variant="text" startIcon={<AddIcon />} onClick={onAddDraft} disabled={disabled}>
                Add rate plan
              </Button>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
